import React from 'react';
import {Box, Button, Typography} from '@mui/material';
import {merge} from 'lodash-es';
import moment from 'moment';
import {useNavigate} from 'react-router-dom';
import {precursorAndHeaderFieldsType} from '../../pages/PrecursorWizard/fields/precursorFields';
import withAutoFormNew, {AutoFormProps} from '../AutoForm/AutoFormNew';
import {WizardField} from '../Wizard/QuestionWrapper';
import {Wizard} from '../Wizard/Wizard';
import {PrecursorWizardSummary} from './PrecursorWizardSummary';
import {useAutoForm} from '../AutoForm/useAutoForm';
import {useAccountContext} from '../../contexts/AccountContext';
import {FeatureBoundary} from '../../contexts/FeatureBoundary';
import {GlobalPermissionEnum} from '../../schema-types/globalTypes';
import {AccountBasicFragment_contacts} from '../../schema-types/AccountBasicFragment';
import {LoadAllDummyDataButton} from '../LoadAllDummyDataButton';
import {UPDATES} from '../../shared/UPDATES';
import {useRequirementsDentistOrBAU} from '../../hooks/useRequirementsDentistOrBAU';
import {useContactIdAndReviewFor} from '../../hooks/useUpdatePrecursor';

export const PrecursorDebug: React.FC<{
  contact: AccountBasicFragment_contacts | null;
}> = ({contact}) => {
  const {values, setValues} = useAutoForm<precursorAndHeaderFieldsType>();
  const {reviewFor} = useContactIdAndReviewFor(values);
  const setPlannedFor = () => {
    setValues(UPDATES._3250, (curr) => {
      const newValues: Partial<precursorAndHeaderFieldsType> = {
        step: 20,
        previousCommunications: [],
        isReviewFocused: 'No',
        meetingHappened: 'Yes',
        bookedMeetingDate: moment().add('M', 1).toDate(),
        meetingType: 'Face to face',
        opportunityValue: '0',
        reviewContact: contact?.id,
        reviewFor,
      };
      return merge({}, curr, newValues);
    });
  };
  const setHappenedToday = () => {
    setValues(UPDATES._3251, (curr) => {
      const newValues: Partial<precursorAndHeaderFieldsType> = {
        reviewContact: contact?.id,
        step: 20,
        previousCommunications: [],
        isReviewFocused: 'No',
        name: 'Test ad-hoc review',
        meetingHappened: 'Yes',
        pastMeetingDate: moment().toDate(),
        meetingType: 'Face to face',
        opportunityValue: '5',
        reviewFor,
      };
      return merge({}, curr, newValues);
    });
  };

  return (
    <Box display="flex">
      <Button
        sx={{mb: 0.5}}
        color="secondary"
        variant="text"
        size="small"
        onClick={() => setPlannedFor()}
      >
        Planned: {contact?.simplifiedName || 'joint'}
      </Button>{' '}
      <Button
        sx={{mb: 0.5}}
        color="secondary"
        variant="text"
        size="small"
        onClick={() => setHappenedToday()}
      >
        Happened: {contact?.simplifiedName || 'joint'}
      </Button>
    </Box>
  );
};

const PrecursorForm: React.FC<
  AutoFormProps<precursorAndHeaderFieldsType, WizardField<precursorAndHeaderFieldsType>>
> = ({formFields}) => {
  const {account} = useAccountContext();
  const navigate = useNavigate();
  const {recommendsGridUrl} = useRequirementsDentistOrBAU();
  return (
    <>
      <Wizard
        goTo="precursor"
        formFields={formFields}
        SummaryComponent={PrecursorWizardSummary}
      />
      <FeatureBoundary
        showErrorIfDenied={false}
        features={[GlobalPermissionEnum.can_restore_admin_permission]}
      >
        <Box position="absolute" top={240} left={200}>
          <Typography>Tester feature: skip precursor</Typography>

          <Box display="flex" flexWrap="wrap">
            <LoadAllDummyDataButton
              readOnly={false}
              incrementRefreshKey={() => {}}
              onCompleted={() => navigate({pathname: `../${recommendsGridUrl}`})}
            />
            {account?.contacts?.length === 1 ? (
              <PrecursorDebug contact={account.contacts[0]} />
            ) : (
              <>
                {account?.contacts?.map((contact) => (
                  <PrecursorDebug key={contact.id} contact={contact} />
                ))}
                <PrecursorDebug contact={null} />
              </>
            )}
          </Box>
        </Box>
      </FeatureBoundary>
    </>
  );
};

export default withAutoFormNew<
  precursorAndHeaderFieldsType,
  unknown,
  WizardField<precursorAndHeaderFieldsType>
>(PrecursorForm);
